import { FaroTilesActionBar } from "@components/common/faro-card/faro-tiles-action-bar";
import { ProjectCard } from "@components/common/project-card/project-card";
import { CardSkeleton } from "@components/common/skeleton-loading/card-skeleton";
import { ProjectsSort } from "@components/projects/projects-sort";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { PaginatedProjects } from "@custom-types/project-types";
import { Box } from "@mui/material";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { UI_HEIGHTS_VIRTUALIZATION_GRID } from "@styles/common-styles";

type Props = Pick<
  PaginatedProjects,
  | "companyId"
  | "projects"
  | "projectArchivingState"
  | "sortBy"
  | "setSortBy"
  | "loadMoreProjects"
> & {
  /** Flag whether it is loading some content  */
  isLoading: boolean;
};

/**
 * Total vertical UI height above the VirtualizedGrid.
 * The grid fills the rest of the viewport height.
 */
const VIRTUAL_GRID_OFFSET =
  UI_HEIGHTS_VIRTUALIZATION_GRID.appBar +
  UI_HEIGHTS_VIRTUALIZATION_GRID.tabBar +
  UI_HEIGHTS_VIRTUALIZATION_GRID.bulkActionsBar +
  UI_HEIGHTS_VIRTUALIZATION_GRID.contentPadding +
  UI_HEIGHTS_VIRTUALIZATION_GRID.paddingTiles;

/**
 * The projects tab listing all the active or archived projects (depending on the props) in the company
 */
export function ProjectsTiles({
  isLoading,
  projects,
  companyId,
  loadMoreProjects,
  projectArchivingState,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const selectedSdbCompanyName = useAppSelector(selectedSdbCompanyNameSelector);

  /** Determine whether to show the export button */
  const shouldShowExportButton = isLoading || projects.length > 0;

  /** Right actions for the tiles action bar */
  const rightActions = shouldShowExportButton
    ? [
        <BulkExportProjects
          key="ExportMembersAsCSV"
          companyId={companyId}
          selectedProjects={projects}
          subjectType="workspace"
          entityName={selectedSdbCompanyName ?? ""}
          projectStatus={projectArchivingState}
          /* It is false since entities can't be selected in tiles view */
          areEntitiesSelected={false}
        />,
      ]
    : [];

  return (
    <Box component="div" padding="0.063rem">
      <FaroTilesActionBar
        leftActions={[
          <ProjectsSort
            key="projects-sort"
            sortBy={sortBy}
            setSortBy={setSortBy}
          />,
        ]}
        rightActions={rightActions}
        isLoading={isLoading}
      />

      <VirtualizedGrid
        // The `key` here forces React to remount the grid whenever `sortBy` changes.
        // This ensures that the scroll position resets and the grid re-renders from the first item
        key={sortBy}
        items={projects}
        isLoading={isLoading}
        loadMoreItems={loadMoreProjects}
        renderSkeletonItem={(i) => (
          <CardSkeleton key={`skeleton-project-${i}`} type="project" />
        )}
        height={`calc(100vh - ${VIRTUAL_GRID_OFFSET}px)`}
        renderItem={(project) => (
          <ProjectCard
            key={project.id}
            project={project}
            companyId={companyId}
            numberOfProjects={projects.length}
          />
        )}
      />
    </Box>
  );
}
